import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { 
  FileText, 
  Calendar, 
  User, 
  Clock,
  Edit,
  Eye,
  Trash2,
  Plus,
  Filter,
  Search
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import WSRGenerator from './WSRGenerator';
import useWSRActions from '../hooks/useWSRActions';
import WSRApiService from '../services/wsrApi';

interface WSRSummary {
  id: number;
  client_id: number;
  client_name: string;
  week_ending: string;
  reporting_period: string;
  project_sponsor: string;
  prepared_by: string;
  status: 'draft' | 'submitted' | 'approved' | 'archived';
  created_at: string;
  updated_at: string;
  accomplished_count: number;
  targets_count: number;
  risks_count: number;
  metrics_count: number;
}

interface WSRManagerProps {
  clientId: number;
  clientName?: string;
}

const WSRManager: React.FC<WSRManagerProps> = ({ clientId, clientName = 'Costco' }) => {
  const [wsrs, setWSRs] = useState<WSRSummary[]>([]);
  const [currentWSR, setCurrentWSR] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showGenerator, setShowGenerator] = useState(false);
  const [editingWSR, setEditingWSR] = useState<any>(null);
  const { addToast } = useToast();

  // Use WSR actions hook for centralized navbar integration
  const { uploadedAnalysis, handleExport, handleShare, handlePrint } = useWSRActions({
    currentWSR,
    onWSRUpdate: (updatedWSR) => {
      setCurrentWSR(updatedWSR);
      loadWSRs(); // Refresh list
    }
  });

  // Load WSRs on component mount
  useEffect(() => {
    loadWSRs();
  }, [clientId]);

  // Handle uploaded analysis from navbar
  useEffect(() => {
    if (uploadedAnalysis && !showGenerator) {
      setShowGenerator(true);
      addToast('📋 Ready to create new WSR with uploaded data', 'info');
    }
  }, [uploadedAnalysis, showGenerator, addToast]);

  const wsrApi = WSRApiService.getInstance();

  const loadWSRs = async () => {
    setIsLoading(true);
    try {
      const response = await wsrApi.getWSRs(clientId);
      setWSRs(response.data || []);
    } catch (error) {
      console.error('Error loading WSRs:', error);
      addToast('❌ Failed to load WSRs', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingWSR(null);
    setCurrentWSR(null);
    setShowGenerator(true);
  };

  const handleEdit = async (wsrId: number) => {
    try {
      const response = await wsrApi.getWSR(wsrId);
      setEditingWSR(response);
      setCurrentWSR(response);
      setShowGenerator(true);
    } catch (error) {
      console.error('Error loading WSR:', error);
      addToast('❌ Failed to load WSR for editing', 'error');
    }
  };

  const handleView = async (wsrId: number) => {
    try {
      const response = await wsrApi.getWSR(wsrId);
      setCurrentWSR(response);
      setShowGenerator(false);
      // Could show a read-only view component here
    } catch (error) {
      console.error('Error loading WSR:', error);
      addToast('❌ Failed to load WSR', 'error');
    }
  };

  const handleDelete = async (wsrId: number) => {
    if (!confirm('Are you sure you want to delete this WSR?')) return;

    try {
      await wsrApi.deleteWSR(wsrId);
      addToast('✅ WSR deleted successfully', 'success');
      loadWSRs();
    } catch (error) {
      console.error('Error deleting WSR:', error);
      addToast('❌ Failed to delete WSR', 'error');
    }
  };

  const handleSaveWSR = async (wsrData: any) => {
    try {
      let response;
      if (editingWSR?.id) {
        response = await wsrApi.updateWSR(editingWSR.id, wsrData);
        addToast('✅ WSR updated successfully', 'success');
      } else {
        response = await wsrApi.createWSR(wsrData);
        addToast('✅ WSR created successfully', 'success');
      }
      
      setCurrentWSR(response);
      setShowGenerator(false);
      loadWSRs();
    } catch (error) {
      console.error('Error saving WSR:', error);
      throw error; // Re-throw to let WSRGenerator handle the error display
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'submitted': return 'bg-shiftleft-100 text-shiftleft-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'archived': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (showGenerator) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Button 
            variant="outline" 
            onClick={() => setShowGenerator(false)}
          >
            ← Back to WSR List
          </Button>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => handleExport('markdown')}
              disabled={!currentWSR}
            >
              Export
            </Button>
            <Button 
              variant="outline" 
              onClick={handleShare}
              disabled={!currentWSR?.id}
            >
              Share
            </Button>
            <Button 
              variant="outline" 
              onClick={handlePrint}
            >
              Print
            </Button>
          </div>
        </div>
        
        <WSRGenerator
          clientId={clientId}
          onSave={handleSaveWSR}
          initialData={editingWSR}
          uploadedAnalysis={uploadedAnalysis}
        />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Weekly Status Reports</h1>
          <p className="text-gray-600">{clientName} - WSR Management</p>
        </div>
        <Button onClick={handleCreateNew} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Create New WSR
        </Button>
      </div>

      {/* WSR List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Recent WSRs
            </span>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Filter className="w-4 h-4 mr-2" />
                Filter
              </Button>
              <Button variant="outline" size="sm">
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-shiftleft-600 mx-auto"></div>
              <p className="text-gray-600 mt-2">Loading WSRs...</p>
            </div>
          ) : wsrs.length === 0 ? (
            <div className="text-center py-8">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">No WSRs found</p>
              <Button onClick={handleCreateNew}>
                Create Your First WSR
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {wsrs.map((wsr) => (
                <div key={wsr.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-lg">
                          {wsr.reporting_period}
                        </h3>
                        <Badge className={getStatusColor(wsr.status)}>
                          {wsr.status.toUpperCase()}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          <span>Week Ending: {formatDate(wsr.week_ending)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>By: {wsr.prepared_by}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>Updated: {formatDate(wsr.updated_at)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <User className="w-4 h-4" />
                          <span>Sponsor: {wsr.project_sponsor}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          {wsr.accomplished_count} Accomplished
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-shiftleft-500 rounded-full"></span>
                          {wsr.targets_count} Targets
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                          {wsr.risks_count} Risks
                        </span>
                        <span className="flex items-center gap-1">
                          <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                          {wsr.metrics_count} Metrics
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleView(wsr.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(wsr.id)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(wsr.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default WSRManager;